import { createContext, useCallback, useContext, useMemo } from "react";
import { toast } from "sonner";
import {
  useMyHabits,
  useMyNotifications,
  useAddHabit,
  useRemoveHabit,
  useUpdateHabitStart,
  useUpdateNotifications,
  useSendNudge,
} from "@/lib/hooks";
import { useAuth } from "@/store/AuthContext";

const AppContext = createContext(null);

const DEFAULT_NOTIFICATIONS = {
  calendarPush: true,
  peerNudges: true,
  weeklyDigest: false,
};

export function AppProvider({ children }) {
  const { user, login } = useAuth();
  const authed = !!user;

  const { data: remoteHabits } = useMyHabits();
  const { data: remoteNotifs } = useMyNotifications();
  const addMut = useAddHabit();
  const removeMut = useRemoveHabit();
  const updateStartMut = useUpdateHabitStart();
  const updateNotifsMut = useUpdateNotifications();
  const nudgeMut = useSendNudge();

  const scheduled = authed ? (remoteHabits || []) : [];
  const notifications = authed ? (remoteNotifs || DEFAULT_NOTIFICATIONS) : DEFAULT_NOTIFICATIONS;

  const requireAuthToast = useCallback(() => {
    toast("Sign in to save", {
      description: "Your rhythm follows you once you sign in.",
      action: { label: "Sign in", onClick: () => login() },
    });
  }, [login]);

  const addHabit = useCallback(
    (templateId, start) => {
      if (!authed) return requireAuthToast();
      addMut.mutate({ template_id: templateId, start });
    },
    [authed, addMut, requireAuthToast],
  );

  const removeHabit = useCallback(
    (templateId) => {
      if (!authed) return requireAuthToast();
      removeMut.mutate(templateId);
    },
    [authed, removeMut, requireAuthToast],
  );

  const updateStart = useCallback(
    (templateId, start) => {
      if (!authed) return requireAuthToast();
      updateStartMut.mutate({ template_id: templateId, start });
    },
    [authed, updateStartMut, requireAuthToast],
  );

  const setNotifications = useCallback(
    (updater) => {
      if (!authed) return requireAuthToast();
      const next = typeof updater === "function" ? updater(notifications) : updater;
      const patch = {};
      Object.keys(next).forEach((k) => {
        if (next[k] !== notifications[k]) patch[k] = next[k];
      });
      if (Object.keys(patch).length) updateNotifsMut.mutate(patch);
    },
    [authed, notifications, updateNotifsMut, requireAuthToast],
  );

  const sendNudge = useCallback(
    ({ recipient_id, recipient_name, habit_id, note } = {}) => {
      if (!authed) {
        toast("Sign in to nudge", {
          description: `${recipient_name || "This member"} won't feel it until you sign in.`,
          action: { label: "Sign in", onClick: () => login() },
        });
        return;
      }
      nudgeMut.mutate(
        { recipient_id, habit_id, note },
        {
          onSuccess: (data) =>
            toast("Nudge sent", {
              description: `${data.recipient_name} will feel a gentle pulse.`,
            }),
          onError: (e) =>
            toast.error("Nudge failed", {
              description: e?.response?.data?.detail || e.message,
            }),
        },
      );
    },
    [authed, nudgeMut, login],
  );

  const value = useMemo(
    () => ({
      scheduled,
      addHabit,
      removeHabit,
      updateStart,
      notifications,
      setNotifications,
      sendNudge,
      requireAuthToast,
      authed,
    }),
    [scheduled, notifications, addHabit, removeHabit, updateStart, setNotifications, sendNudge, requireAuthToast, authed],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used inside AppProvider");
  return ctx;
};
